import { useState, useMemo } from 'react';
import styles from './CategoryBuckets.module.css';

export interface Category {
  id: string;
  name: string;
}

export interface CategoryBucketsProps {
  categories: Category[];
  expandable?: boolean;
  className?: string;
}

type BucketType = 'fiction' | 'nonfiction' | 'childrens' | 'others';

/**
 * Auto-bucket categories based on text matching
 * Algorithm based on REPORT-CARD-AND-QC-WORKFLOW-PLAN.md
 */
function bucketCategory(name: string): BucketType {
  const lowerName = name.toLowerCase();

  // Fiction
  if (
    lowerName.includes('fiction') ||
    lowerName.includes('novel') ||
    lowerName.includes('story') ||
    lowerName.includes('romance') ||
    lowerName.includes('mystery') ||
    lowerName.includes('thriller') ||
    lowerName.includes('sci-fi') ||
    lowerName.includes('science fiction') ||
    lowerName.includes('fantasy')
  ) {
    return 'fiction';
  }

  // Children & YA
  if (
    lowerName.includes('children') ||
    lowerName.includes('ya ') ||
    lowerName.includes('young adult') ||
    lowerName.includes('juvenile') ||
    lowerName.includes('middle grade') ||
    lowerName.includes('picture book')
  ) {
    return 'childrens';
  }

  // Non-Fiction
  if (
    lowerName.includes('nonfiction') ||
    lowerName.includes('non-fiction') ||
    lowerName.includes('biography') ||
    lowerName.includes('memoir') ||
    lowerName.includes('history') ||
    lowerName.includes('science') ||
    lowerName.includes('business') ||
    lowerName.includes('self-help') ||
    lowerName.includes('cookbook') ||
    lowerName.includes('travel')
  ) {
    return 'nonfiction';
  }

  // Others (default)
  return 'others';
}

const bucketLabels: Record<BucketType, string> = {
  fiction: 'Fiction',
  nonfiction: 'Non-Fiction',
  childrens: 'Children & YA',
  others: 'Others'
};

/**
 * CategoryBuckets - Auto-bucket categories with expand/collapse
 *
 * Buckets: Fiction, Non-Fiction, Children & YA, Others
 * Auto-detects based on category name text matching
 */
export function CategoryBuckets({
  categories,
  expandable = true,
  className = ''
}: CategoryBucketsProps) {
  const [expandedBuckets, setExpandedBuckets] = useState<Set<BucketType>>(new Set());

  const buckets = useMemo(() => {
    const result: Record<BucketType, Category[]> = {
      fiction: [],
      nonfiction: [],
      childrens: [],
      others: []
    };

    categories.forEach((cat) => {
      const bucket = bucketCategory(cat.name);
      result[bucket].push(cat);
    });

    return result;
  }, [categories]);

  const toggleBucket = (bucket: BucketType) => {
    if (!expandable) return;

    setExpandedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) {
        next.delete(bucket);
      } else {
        next.add(bucket);
      }
      return next;
    });
  };

  if (categories.length === 0) {
    return (
      <div className={`${styles.emptyState} ${className}`}>
        <span className={styles.emptyText}>No categories available</span>
      </div>
    );
  }

  return (
    <div className={`${styles.bucketContainer} ${className}`}>
      {(Object.keys(buckets) as BucketType[]).map((bucketKey) => {
        const bucketCategories = buckets[bucketKey];
        if (bucketCategories.length === 0) return null;

        const isExpanded = expandedBuckets.has(bucketKey);

        return (
          <div key={bucketKey} className={styles.bucket}>
            <button
              type="button"
              onClick={() => toggleBucket(bucketKey)}
              className={styles.bucketButton}
              disabled={!expandable}
            >
              <span className={styles.bucketIcon}>
                {expandable && (isExpanded ? '▼' : '▶')}
              </span>
              <span className={styles.bucketLabel}>
                {bucketLabels[bucketKey]} ({bucketCategories.length})
              </span>
            </button>

            {(isExpanded || !expandable) && (
              <ul className={styles.categoryList}>
                {bucketCategories.map((cat) => (
                  <li key={cat.id} className={styles.categoryItem}>
                    {cat.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CategoryBuckets;
